import type { FC } from 'react'

import Image from 'next/image'
import { ArrowRight, Clock, MapPin } from 'lucide-react'
import { cn } from '@/lib/utils'
import { toFaDigits } from '@/lib/format'
import { scrollToId } from '@/lib/scroll'
import { animationDelay } from '@/lib/motion'
import { formatRange, getHoursFor } from '@/lib/hours'
import { useBakeryClock } from '@/hooks/use-bakery-clock'
import { ButtonLink } from '@/components/generals/button'
import { OpenStatus } from '@/components/generals/open-status'

import { SHELL, bakery } from '@/constants/site'

const InfoStrip: FC = () => {
    const now = useBakeryClock()
    const today = now ? getHoursFor(now.weekday) : undefined

    return (
        <div className='border-b border-line'>
            <dl className={cn(SHELL, 'grid divide-y divide-line text-[0.9375rem] sm:grid-cols-3 sm:divide-x sm:divide-y-0')}>
                <div className='flex items-center gap-3 py-4 sm:pe-6'>
                    <dt className='flex items-center gap-3'>
                        <Clock className='size-4 shrink-0 text-crust-soft' aria-hidden='true' />
                        <span className='label-caps text-crust-soft'>امروز</span>
                    </dt>

                    <dd className='tabular-nums'>
                        {now ? formatRange(today) : 'هر روز'}
                    </dd>
                </div>

                <div className='flex items-center py-4 sm:px-6'>
                    <dt className='sr-only'>نشانی</dt>

                    <dd className='flex items-center gap-3'>
                        <MapPin className='size-4 shrink-0 text-crust-soft' aria-hidden='true' />

                        <a href={bakery.directionsUrl} target='_blank' rel='noopener noreferrer' className='link-underline' dir='ltr'>
                            {bakery.shortAddress}
                        </a>
                    </dd>
                </div>

                <div className='flex items-center py-4 sm:justify-end sm:ps-6'>
                    <dt className='sr-only'>وضعیت همین حالا</dt>
                    <dd>
                        <OpenStatus />
                    </dd>
                </div>
            </dl>
        </div>
    )
}

export const Hero: FC = () => (
    <section id='hero' aria-labelledby='hero-title'>
        <div className={cn(SHELL, 'pt-10 pb-9 sm:pt-14 lg:pt-16 lg:pb-12')}>
            <p className='ld-rise label-caps text-crust-soft'>
                نانوایی محله‌ای در {bakery.neighborhood} · از سال {toFaDigits(bakery.founded)}
            </p>

            <div className='mt-5 grid gap-x-10 gap-y-7 lg:grid-cols-12 lg:items-end'>
                <h1 id='hero-title' className='font-heading text-[3.4rem] leading-[1.2] font-normal tracking-[-0.015em] sm:text-[5rem] lg:col-span-8 lg:text-[6.25rem] xl:text-[7rem]'>
                    <span className='ld-rise block' style={animationDelay(120)}>آهسته می‌پزیم.</span>
                    <span className='ld-rise block italic' style={animationDelay(240)}>زود تمام می‌شود.</span>
                </h1>

                <div className='lg:col-span-4 lg:pb-2'>
                    <p className='ld-rise max-w-sm text-[1.0625rem] leading-relaxed text-crust-soft' style={animationDelay(360)}>
                        نان با خمیرترش طبیعی، شیرینی‌های لایه‌ای و چند کیک خوب؛ همه پیش از طلوع آفتاب، در لیندن رو پخته می‌شوند.
                    </p>

                    <div className='ld-rise mt-7 flex flex-wrap gap-3' style={animationDelay(460)}>
                        <ButtonLink href='/order' className='sm:h-14 sm:px-8 sm:text-[0.9375rem]'>
                            پیش‌سفارش بدهید
                            <ArrowRight className='size-4 transition-transform duration-500 ease-(--ease-soft) rtl:-scale-x-100 group-hover/button:translate-x-0.5 rtl:group-hover/button:-translate-x-0.5' aria-hidden='true' />
                        </ButtonLink>

                        <ButtonLink
                            href='#todays-bake'
                            variant='outline'
                            className='sm:h-14 sm:px-8 sm:text-[0.9375rem]'
                            onClick={event => {
                                event.preventDefault()
                                scrollToId('todays-bake')
                            }}
                        >
                            پخت امروز را ببینید
                        </ButtonLink>
                    </div>
                </div>
            </div>
        </div>

        <div className='ld-fade relative h-[min(68svh,34rem)] overflow-hidden bg-flour-deep sm:h-[min(70svh,44rem)] lg:h-[clamp(26rem,calc(100svh-29rem),48rem)]' style={animationDelay(200)}>
            <div className='ld-settle absolute inset-0'>
                <Image
                    src='/images/hero-sourdough.jpg'
                    alt='یک نان روستایی خمیرترش تازه از تنور با شیار بازِ رویش، روی پارچه‌ی کتان بر پیشخوانی آردپاشیده'
                    fill
                    preload
                    fetchPriority='high'
                    sizes='100vw'
                    className='photo-grade object-cover object-[60%_50%]'
                />
            </div>
        </div>

        <InfoStrip />
    </section>
)
